import { buildAgentMessage, buildResponseEnvelope } from '../helpers';
// ---------------------------------------------------------------------------
// Approval-block de-duplication (IP-14386)
//
// Durable approvals (IP-14361/2) persist the confirmation/execution_plan block
// to history. On a refresh or new-tab open, the widget BOTH loads that block
// from history AND replays the buffered `approval_required` SSE event — so the
// same approval card renders twice ("Office Assistant" + "Assistant"), both
// still actionable. We keep the persisted/history copy (it carries the resolved
// `status` annotation that disables the buttons) and drop the live duplicate.
// ---------------------------------------------------------------------------
/**
 * Every block type that renders an approve/cancel gate. This is the single
 * source of truth: any consumer that reasons about "is this an approval?" must
 * key off this set rather than testing `block.type === 'confirmation'` by hand.
 * Adding a new approval block type here is what makes the freeze/dedup paths —
 * and their contract tests — pick it up (IP-15374).
 */
export const APPROVAL_BLOCK_TYPES = new Set(['confirmation', 'execution_plan']);
/** True when this block renders an approve/cancel gate the user must act on. */
export function isApprovalBlock(block) {
    return APPROVAL_BLOCK_TYPES.has(block.type);
}
/**
 * The UndoData an `undo` metadata block merges into its preceding block.
 *
 * Single source of truth for BOTH paths that do this merge: the live SSE
 * reducer in useBlockStream and normalizeHistoryMetadataBlocks for persisted
 * history. They used to spell the same object literal out twice, so a field
 * added to one was silently dropped by the other — and history is the path that
 * matters here, since the IP-15624 bug only appears after a refresh. A field
 * added below now reaches both.
 */
export function buildUndoData(block) {
    return {
        action_id: block.action_id,
        summary: block.summary,
        undo_available: block.undo_available,
        expires_at: block.expires_at,
        undo_window_minutes: block.undo_window_minutes,
        undone: block.undone
    };
}
// ---------------------------------------------------------------------------
// Live undo settlement (IP-15624)
//
// The undo card settles itself when the user CLICKS it. Reversing the same
// operation by TYPING ("undo that") produced no client-side signal at all, so
// the card already on screen kept offering a live button and a running
// countdown over an operation that had just been reverted; clicking it came
// back with "This operation has already been undone." The orchestrator now
// emits `undo_settled` naming the tokens it settled, and the helpers below
// apply that to whatever is already rendered.
//
// Two block shapes carry an undo, and both have to be handled — this is the
// same split `buildUndoData` exists for. Persisted history holds the standalone
// `undo` block (the merge into the preceding block happens on read, in
// normalizeHistoryMetadataBlocks); live and optimistically-appended messages
// hold the already-merged `undoData`. Settling only one of them fixes the bug
// on one path and leaves it live on the other.
// ---------------------------------------------------------------------------
/**
 * The undo token a block's affordance is keyed by, or null when it carries no
 * undo. Single source of truth for matching `undo_settled.action_ids` against
 * rendered blocks — callers must not reach into `action_id`/`undoData` by hand.
 *
 * Tokens are compared verbatim: `bulk:277` and `277` are different affordances,
 * and the `bulk:` prefix is part of the identity, never stripped.
 */
export function undoTokenForBlock(block) {
    var _a;
    if (block.type === 'undo')
        return block.action_id || null;
    return ((_a = block.undoData) === null || _a === void 0 ? void 0 : _a.action_id) || null;
}
/**
 * The settled tokens named by an `undo_settled` payload, or an empty set when
 * the event says nothing actionable.
 *
 * `undone !== true` is dropped rather than applied in reverse: settling is
 * terminal, so no event is allowed to turn a settled card back into a live one.
 */
export function parseUndoSettledTokens(payload) {
    const p = payload;
    if (!p || p.undone !== true || !Array.isArray(p.action_ids))
        return new Set();
    return new Set(p.action_ids.filter((id) => typeof id === 'string' && id.length > 0));
}
/** The settled shape of an undo — identical to what the server replays after a refresh. */
function settledUndoFields() {
    return { undone: true, undo_available: false };
}
/**
 * Mark every block whose undo token is in `settledTokens` as undone.
 *
 * Returns the input array unchanged when nothing matched, so a repeat event (or
 * one naming tokens that are not on screen) is a genuine no-op — no new object,
 * no re-render, no churn through the memoized render path.
 */
export function settleUndoInBlocks(blocks, settledTokens) {
    if (!Array.isArray(blocks) || blocks.length === 0 || settledTokens.size === 0)
        return blocks;
    let changed = false;
    const next = blocks.map((block) => {
        const token = undoTokenForBlock(block);
        if (!token || !settledTokens.has(token))
            return block;
        if (block.type === 'undo') {
            if (block.undone === true)
                return block;
            changed = true;
            return Object.assign(Object.assign({}, block), settledUndoFields());
        }
        const { undoData } = block;
        if (!undoData || undoData.undone === true)
            return block;
        changed = true;
        return Object.assign(Object.assign({}, block), { undoData: Object.assign(Object.assign({}, undoData), settledUndoFields()) });
    });
    return changed ? next : blocks;
}
/**
 * Settle the undo affordances in a single chat message. Returns the input
 * message unchanged when nothing matched (see settleUndoInBlocks).
 */
export function settleUndoInMessage(message, settledTokens) {
    var _a;
    const blocks = (_a = message.envelope) === null || _a === void 0 ? void 0 : _a.blocks;
    if (!message.envelope || !Array.isArray(blocks))
        return message;
    const settledBlocks = settleUndoInBlocks(blocks, settledTokens);
    if (settledBlocks === blocks)
        return message;
    return Object.assign(Object.assign({}, message), { envelope: Object.assign(Object.assign({}, message.envelope), { blocks: settledBlocks }) });
}
/** Stable identity for an approval block: approval_id (confirmation) or plan_id (execution_plan). */
export function approvalBlockKey(block) {
    if (block.type === 'confirmation') {
        return block.approval_id || null;
    }
    if (block.type === 'execution_plan') {
        return block.plan_id || null;
    }
    return null;
}
/**
 * Durable resolution status shared by every approval block type. Absent or
 * 'pending' means the card is still actionable.
 */
export function approvalBlockStatus(block) {
    return block.status;
}
/** Collect the approval identities already present in persisted history messages. */
export function collectHistoryApprovalKeys(historyMessages) {
    const keys = new Set();
    historyMessages.forEach((message) => {
        var _a;
        const { blocks } = (_a = message.envelope) !== null && _a !== void 0 ? _a : {};
        if (!Array.isArray(blocks))
            return;
        blocks.forEach((block) => {
            const key = approvalBlockKey(block);
            if (key)
                keys.add(key);
        });
    });
    return keys;
}
/**
 * Return the live agent message with any approval block whose identity already
 * exists in history removed. Returns the input unchanged when there is nothing
 * to strip, so referential equality (and memoization) is preserved on the common
 * path. Returns null when stripping empties the message of renderable blocks.
 */
export function stripDuplicateApprovalBlocks(liveMessage, historyApprovalKeys) {
    if (!(liveMessage === null || liveMessage === void 0 ? void 0 : liveMessage.envelope) || historyApprovalKeys.size === 0)
        return liveMessage;
    const { blocks } = liveMessage.envelope;
    if (!Array.isArray(blocks) || blocks.length === 0)
        return liveMessage;
    const filtered = blocks.filter((block) => {
        if (!APPROVAL_BLOCK_TYPES.has(block.type))
            return true;
        const key = approvalBlockKey(block);
        return !(key && historyApprovalKeys.has(key));
    });
    if (filtered.length === blocks.length)
        return liveMessage;
    if (filtered.length === 0)
        return null;
    return Object.assign(Object.assign({}, liveMessage), { envelope: Object.assign(Object.assign({}, liveMessage.envelope), { blocks: filtered }) });
}
// ---------------------------------------------------------------------------
// Response-level message de-duplication (IP-14386, follow-up)
//
// After a durable-approval *continuation* turn (the post-approve/cancel "Done."
// + result card + undo) the orchestrator does two things: it persists the
// assistant message to history AND it publishes the same blocks over the live
// SSE channel (which a second open tab receives, or which the acting tab
// optimistically appends on stream_end). The two copies share a `response_id`
// but carry different message `id`s:
//
//   • persisted/history copy → id = ai_messages UUID, envelope.response_id = R,
//     agent badge resolved to "Office Assistant".
//   • live / optimistically-appended copy → id === envelope.response_id === R,
//     agent badge unresolved → "Assistant".
//
// De-duping on `id` alone never collapses them, so the continuation renders
// twice ("Office Assistant: Done." + "Assistant: Done."). We collapse on the
// response identity instead, always preferring the persisted copy.
// ---------------------------------------------------------------------------
/**
 * Response identity for a message: its envelope `response_id` when present,
 * otherwise its `id`. Two renderings of the same agent response share this key.
 */
function messageResponseKey(message) {
    var _a;
    return ((_a = message.envelope) === null || _a === void 0 ? void 0 : _a.response_id) || message.id;
}
/** True for a live / optimistically-appended copy (id mirrors the response_id). */
function isLiveCopy(message) {
    var _a;
    const responseId = (_a = message.envelope) === null || _a === void 0 ? void 0 : _a.response_id;
    return Boolean(responseId) && message.id === responseId;
}
/**
 * Collect the response_ids that already have a *persisted* (server) copy in
 * history — i.e. a message whose `id` differs from its `response_id`. These are
 * the rows the orchestrator wrote to ai_messages; the live SSE copy of the same
 * response should be suppressed in favour of them.
 */
export function collectPersistedResponseIds(messages) {
    const ids = new Set();
    messages.forEach((message) => {
        var _a;
        const responseId = (_a = message.envelope) === null || _a === void 0 ? void 0 : _a.response_id;
        if (responseId && message.id !== responseId)
            ids.add(responseId);
    });
    return ids;
}
/**
 * Collapse messages that share a response identity, preferring the persisted
 * server copy over a live / optimistically-appended duplicate. Messages without
 * a response_id (e.g. user messages) are keyed by `id`, preserving the previous
 * id-only de-dup behaviour. Order is preserved.
 */
export function dedupeMessagesByResponseId(messages) {
    const persistedResponseIds = collectPersistedResponseIds(messages);
    const seen = new Set();
    const deduped = [];
    messages.forEach((message) => {
        // Drop the live/optimistic copy when a persisted copy of the same response
        // exists — the persisted copy carries the correct agent badge and resolved
        // (button-disabled) confirmation state.
        if (isLiveCopy(message) && persistedResponseIds.has(message.id))
            return;
        const key = messageResponseKey(message);
        if (seen.has(key))
            return;
        seen.add(key);
        deduped.push(message);
    });
    return deduped;
}
/**
 * Suppress the entire live agent message when a persisted copy of the same
 * response already exists in history. Covers the window where the live SSE
 * replay/stream renders alongside the persisted history row before stream_end
 * clears it. Returns the live message unchanged when it is genuinely new.
 */
export function suppressLivePersistedMessage(liveMessage, persistedResponseIds) {
    var _a;
    if (!liveMessage)
        return null;
    const responseId = (_a = liveMessage.envelope) === null || _a === void 0 ? void 0 : _a.response_id;
    if (responseId && persistedResponseIds.has(responseId))
        return null;
    return liveMessage;
}
function plainTextFromMarkdownBlocks(blocks) {
    return blocks
        .filter((b) => b.type === 'markdown')
        .map((b) => b.content.trim())
        .filter(Boolean)
        .join('\n\n')
        .trim();
}
// ---------------------------------------------------------------------------
// Build the live (in-progress) agent message from current stream state
// ---------------------------------------------------------------------------
export function buildLiveAgentMessage(streamingMessageId, streamState) {
    var _a;
    if (!streamingMessageId || !streamState.isStreaming)
        return null;
    const liveBlocks = [...streamState.blocks];
    if (streamState.streamingContent && streamState.streamingBlockId) {
        const streamingBlock = {
            type: 'markdown',
            block_id: streamState.streamingBlockId,
            block_index: liveBlocks.length,
            content: streamState.streamingContent,
            streaming: true
        };
        liveBlocks.push(streamingBlock);
    }
    const agentResponse = plainTextFromMarkdownBlocks(liveBlocks);
    const envelope = buildResponseEnvelope({
        responseId: streamState.responseId || '',
        conversationId: streamState.conversationId || '',
        turnNumber: (_a = streamState.turnNumber) !== null && _a !== void 0 ? _a : 0,
        agent: streamState.agent,
        blocks: liveBlocks,
        agentResponse
    });
    return {
        id: streamingMessageId,
        role: 'agent',
        content: '',
        agent: streamState.agent || undefined,
        envelope,
        created_at: new Date().toISOString()
    };
}
export function buildStreamEndMessage(options) {
    const agentResponse = plainTextFromMarkdownBlocks(options.blocks);
    return buildAgentMessage({
        responseId: options.responseId,
        conversationId: options.conversationId,
        turnNumber: options.turnNumber,
        agent: options.agent,
        blocks: options.blocks,
        suggestedActions: options.suggestedActions,
        feedbackId: options.feedbackId,
        durationMs: options.durationMs,
        agentResponse
    });
}
// ---------------------------------------------------------------------------
// Build the cancelled/stopped agent message from partial stream state
// ---------------------------------------------------------------------------
export function buildCancelledMessage(streamState) {
    var _a;
    if (!streamState.responseId)
        return null;
    const finalBlocks = [...streamState.blocks];
    if (streamState.streamingContent && streamState.streamingBlockId) {
        const partialBlock = {
            type: 'markdown',
            block_id: streamState.streamingBlockId,
            block_index: finalBlocks.length,
            content: streamState.streamingContent
        };
        finalBlocks.push(partialBlock);
    }
    if (finalBlocks.length === 0)
        return null;
    const agentResponse = plainTextFromMarkdownBlocks(finalBlocks);
    const message = buildAgentMessage({
        id: streamState.responseId,
        responseId: streamState.responseId,
        conversationId: streamState.conversationId || '',
        turnNumber: (_a = streamState.turnNumber) !== null && _a !== void 0 ? _a : 0,
        agent: streamState.agent,
        blocks: finalBlocks,
        agentResponse
    });
    if (message.envelope) {
        message.envelope.cancelled = true;
    }
    return message;
}
